import { Button, useToast } from '@hkdevs/design-system';
import {
  useExportLineItems,
  useLineItems,
  type LineItemExportCreate,
} from '../../api/queries/search';
import { downloadBlob } from '../payHistory/csv';
import { PAGE_SIZE, searchScope, type LineItemSearchParams } from './LineItemSearchView';
import { withReference } from '../../api/withReference';

const EXPORT_FAILED = {
  tone: 'danger' as const,
  title: 'The PDF couldn’t be made',
  description: 'Nothing was downloaded. Try again, or narrow the filters.',
};

/** The API's body: the search's filters, without the ones left out. */
function exportBody(filters: ReturnType<typeof searchScope>['filters']): LineItemExportCreate {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined));
}

/**
 * The page header's Export PDF (pc-o19.5): every line item the filters match, not just
 * this page. A small export downloads at once; a large one is made in the background
 * (a toast says so) and downloads when it's ready. A failure shows a toast with Retry.
 * Disabled with no matches.
 */
export function ExportLineItems({
  repId,
  supervisor = false,
  search,
}: {
  repId?: string;
  supervisor?: boolean;
  search: LineItemSearchParams;
}) {
  const { filters } = searchScope(repId, supervisor, search);
  // The list's own query (shared), for whether there's anything to export.
  const results = useLineItems({ ...filters, page: search.page ?? 1, pageSize: PAGE_SIZE });
  const { toast } = useToast();
  const create = useExportLineItems({
    onQueued: (queued) =>
      toast({
        tone: 'info',
        title: 'Preparing your export…',
        description: `${queued.rowCount.toLocaleString()} line items. It downloads when it’s ready.`,
      }),
  });

  const start = (): void =>
    create.mutate(exportBody(filters), {
      onSuccess: ({ blob, fileName }) => downloadBlob(blob, fileName),
      onError: (error) =>
        toast(
          withReference({ ...EXPORT_FAILED, action: { label: 'Retry', onClick: start } }, error),
        ),
    });

  return (
    <Button
      size="sm"
      leadingIcon="download"
      onClick={start}
      loading={create.isPending}
      loadingText="Exporting…"
      disabled={!results.data || results.data.total === 0}
    >
      Export PDF
    </Button>
  );
}
